"use client";

import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import { motion, useScroll, useTransform, MotionValue, useMotionValueEvent } from "motion/react";

interface Props {
  children: React.ReactNode[];
  borderRadius?: number;
  onTap?: (index: number) => void;
  className?: string;
  loop?: boolean;
}

export const M3ContainedCarouselOptimised: React.FC<Props> = ({
  children,
  borderRadius = 28,
  onTap,
  className = "",
  loop = false,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollX } = useScroll({ container: overlayRef });

  const weights = useMemo(() => [5, 4, 1], []);
  const visibleCount = weights.length;
  const totalWeight = useMemo(() => weights.reduce((a, b) => a + b, 0), [weights]);
  
  const actualChildrenLength = children.length;
  // If loop is true, create a large virtual length to emulate infinite scrolling.
  const virtualLength = loop && actualChildrenLength > 0 ? actualChildrenLength * 50 : actualChildrenLength;
  const maxProgress = Math.max(0, virtualLength - visibleCount);

  const [unitWidth, setUnitWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    
    const updateWidth = () => {
      const w = containerRef.current!.clientWidth;
      setUnitWidth(w * (weights[0] / totalWeight));
    };

    updateWidth();
    
    const ob = new ResizeObserver(updateWidth);
    ob.observe(containerRef.current);
    
    return () => ob.disconnect();
  }, [weights, totalWeight]);

  const progress = useTransform(scrollX, (x) => {
    if (unitWidth === 0) return 0;
    const maxScrollX = unitWidth * maxProgress;
    const clampedX = Math.max(0, Math.min(x, maxScrollX));
    return clampedX / unitWidth;
  });

  const getWidthUnits = (index: number, p: number) => {
    const position = index - p;
    if (position <= -1) return 0;
    if (position >= weights.length) return 0;

    const i = Math.floor(position);
    const rem = position - i;
    const start = i >= 0 && i < weights.length ? weights[i] : 0;
    const end = (i + 1) >= 0 && (i + 1) < weights.length ? weights[i + 1] : 0;

    return start + (end - start) * rem;
  };

  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    let clientX;
    if ('touches' in e) {
      clientX = e.changedTouches[0].clientX;
    } else {
      clientX = (e as React.MouseEvent).clientX;
    }

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const clickX = clientX - rect.left;
    const p = progress.get();

    let currentX = 0;
    for (let j = Math.max(0, Math.floor(p)); j < virtualLength; j++) {
      const w = (getWidthUnits(j, p) / totalWeight) * rect.width;
      if (clickX >= currentX && clickX <= currentX + w) {
        if (w > 0) onTap?.(j % actualChildrenLength);
        break;
      }
      currentX += w;
      if (currentX > rect.width) break;
    }
  };

  const totalScrollWidth = unitWidth === 0 
    ? "100%" 
    : `calc(100% + ${unitWidth * maxProgress}px)`;

  const [visibleRange, setVisibleRange] = useState({ start: 0, end: visibleCount + 1 });
  
  useMotionValueEvent(progress, "change", (p) => {
    const start = Math.max(0, Math.floor(p) - 1);
    const end = Math.min(virtualLength - 1, Math.ceil(p) + visibleCount);
    if (start !== visibleRange.start || end !== visibleRange.end) {
      setVisibleRange({ start, end });
    }
  });

  const visibleIndices = [];
  for (let i = visibleRange.start; i <= visibleRange.end; i++) {
    if (i < virtualLength) visibleIndices.push(i);
  }

  return (
    <div className={`relative w-full aspect-[2/1] lg:h-[400px] lg:aspect-auto overflow-hidden ${className}`} ref={containerRef}>
      
      <div className="absolute inset-0 block h-full -mr-4 pointer-events-none z-0">
        {visibleIndices.map((i) => (
          <CarouselItem 
            key={i}
            index={i} 
            progress={progress} 
            weights={weights} 
            totalWeight={totalWeight}
            borderRadius={borderRadius}
          >
            {actualChildrenLength > 0 ? children[i % actualChildrenLength] : null}
          </CarouselItem>
        ))}
      </div>

        <div
        ref={overlayRef}
        onClick={handleTap}
        className="absolute inset-0 overflow-x-auto snap-x snap-mandatory flex w-full h-full z-10 select-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        style={{ touchAction: 'pan-x pan-y' }}
      >
        <div style={{ width: totalScrollWidth }} className="h-full flex pointer-events-none">
          {unitWidth > 0 &&
            Array.from({ length: maxProgress + 1 }).map((_, i) => (
              <div
                key={i}
                style={{ width: unitWidth }}
                className="h-full snap-start shrink-0"
              />
            ))}
        </div>
      </div>
    </div>
  );
};

interface ItemProps {
  index: number;
  progress: MotionValue<number>;
  weights: number[];
  totalWeight: number;
  borderRadius: number;
  children: React.ReactNode;
}

const CarouselItem: React.FC<ItemProps> = ({ index, progress, weights, totalWeight, borderRadius, children }) => {
  const getWidthUnits = (idx: number, p: number) => {
    const position = idx - p;
    if (position <= -1) return 0;
    if (position >= weights.length) return 0;

    const i = Math.floor(position);
    const rem = position - i;
    const start = i >= 0 && i < weights.length ? weights[i] : 0;
    const end = (i + 1) >= 0 && (i + 1) < weights.length ? weights[i + 1] : 0;

    return start + (end - start) * rem;
  };

  const widthPercent = useTransform(progress, (p) => {
    return (getWidthUnits(index, p) / totalWeight) * 100;
  });

  const xPercent = useTransform(progress, (p) => {
    let x = 0;
    for (let j = Math.max(0, Math.floor(p)); j < index; j++) {
      x += getWidthUnits(j, p);
    }
    return (x / totalWeight) * 100;
  });

  return (
    <motion.div 
      className="absolute top-0 bottom-0 h-full overflow-hidden origin-left"
      style={{ 
        width: useTransform(widthPercent, w => `${w}%`),
        left: useTransform(xPercent, x => `${x}%`)
      }}
    >
      <div className="w-full h-full pr-4">
        <div
          className="w-full h-full overflow-hidden bg-neutral-800 shadow-md transition-shadow active:scale-[0.98] duration-200"
          style={{ borderRadius }}
        >
          {React.isValidElement(children) ? React.cloneElement(children as React.ReactElement<any>, { widthPercent }) : children}
        </div>
      </div>
    </motion.div>
  );
};
